import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getBillingActionErrorMessage,
  normalizeMarketingBullets,
  getSubscriptionStatusLabel,
  getSubscriptionTimingLabel,
  type CurrentSubscription,
  type PublicBillingPlan,
  useCurrentSubscription,
  useCustomerPortalSessionMutation,
  usePublicBillingPlans,
  useTrialPaymentMethodSessionMutation,
  useTrialPlanSelectionMutation,
  useTrialRetryMutation,
} from "../api/billing";
import {
  clearSelectedPlanKey,
  getCheckoutConfirmationPath,
  getMarketingPlanInterest,
} from "../lib/billingSelection";
import {
  trackPlanSelected,
  trackTrialPaymentMethodAdded,
  trackTrialPlanSelected,
  trackTrialStarted,
} from "../lib/analytics";

const ACTIVE_STATUSES = new Set(["active", "trialing", "manual"]);
const ACTIVATION_PENDING_TIMEOUT_MS = 15000;

function formatDate(value: string | null) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatPlanPrice(plan: Pick<PublicBillingPlan, "currency" | "price_monthly">) {
  if (plan.price_monthly == null) {
    return "Contact us";
  }
  return `${plan.currency ?? "AUD"} $${plan.price_monthly.toFixed(2)} / month`;
}

function formatPlanKeyLabel(planKey: string) {
  return planKey
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatTrialPlanName(subscription: CurrentSubscription) {
  return subscription.trial_plan_key === subscription.plan_key
    ? subscription.plan_name
    : subscription.trial_plan_key
      ? formatPlanKeyLabel(subscription.trial_plan_key)
      : subscription.plan_name;
}

function PlanChoiceCard({
  plan,
  onChoose,
  actionLabel,
  previouslyViewed = false,
}: {
  plan: PublicBillingPlan;
  onChoose: (plan: PublicBillingPlan) => void;
  actionLabel?: string;
  previouslyViewed?: boolean;
}) {
  const marketingBullets = normalizeMarketingBullets(plan.marketing_bullets);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5">{plan.display_name}</Typography>
          {previouslyViewed && <Chip size="small" label="Previously viewed" variant="outlined" />}
        </Box>
        <Typography variant="h6" sx={{ mt: 0.5 }}>
          {formatPlanPrice(plan)}
        </Typography>
        {plan.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {plan.description}
          </Typography>
        )}
      </Box>

      {marketingBullets.length > 0 && (
        <>
          <Divider />
          <Stack spacing={1}>
            {marketingBullets.map((bullet) => (
              <Box key={bullet} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <CheckCircleOutlineRoundedIcon color="success" fontSize="small" sx={{ mt: 0.2 }} />
                <Typography variant="body2">{bullet}</Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}

      <Button variant="contained" onClick={() => onChoose(plan)} sx={{ mt: "auto" }}>
        {actionLabel ?? `Choose ${plan.display_name}`}
      </Button>
    </Box>
  );
}

export function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutParam = searchParams.get("checkout");
  const setupParam = searchParams.get("setup");
  const billingState = searchParams.get("billing_state");
  const checkoutCanceled = checkoutParam === "canceled";
  const isPostCheckout = checkoutParam === "success";
  const [polling, setPolling] = useState(isPostCheckout);
  const [activationTimedOut, setActivationTimedOut] = useState(false);

  const {
    data: subData,
    isLoading,
    isError: subscriptionError,
    refetch: refetchSubscription,
  } = useCurrentSubscription({
    refetchInterval: polling ? 3000 : false,
  });
  const {
    data: publicPlansData,
    isLoading: publicPlansLoading,
    isError: publicPlansError,
    refetch: refetchPublicPlans,
  } = usePublicBillingPlans();
  const customerPortalMutation = useCustomerPortalSessionMutation();
  const trialPlanMutation = useTrialPlanSelectionMutation();
  const trialPaymentMethodMutation = useTrialPaymentMethodSessionMutation();
  const trialRetryMutation = useTrialRetryMutation();

  const subscription = subData?.subscription ?? null;
  const marketingPlanInterest = getMarketingPlanInterest();
  const publicPlans = (publicPlansData?.plans ?? []).filter((plan) => plan.plan_key !== "free");
  const isActiveSubscription = subscription?.status
    ? ACTIVE_STATUSES.has(subscription.status)
    : false;
  const timingLabel = getSubscriptionTimingLabel(subscription);
  const formattedTimingDate = formatDate(subscription?.current_period_end ?? null);
  const statusLabel = getSubscriptionStatusLabel(subscription);
  const isTrialSubscription = subscription?.status === "trialing";
  const isPausedTrial = subscription?.status === "paused";

  useEffect(() => {
    if (isPostCheckout && isActiveSubscription) {
      setPolling(false);
      setActivationTimedOut(false);
      clearSelectedPlanKey();
      setSearchParams(
        billingState ? { billing_state: billingState } : {},
        { replace: true }
      );
    }
  }, [billingState, isActiveSubscription, isPostCheckout, setSearchParams]);

  useEffect(() => {
    if (subscription?.status !== "trialing") {
      return;
    }
    trackTrialStarted({
      trial_plan_id: subscription.trial_plan_id,
      trial_plan_key: subscription.trial_plan_key,
      trial_days: subscription.days_remaining ?? undefined,
      days_remaining: subscription.days_remaining,
    });
  }, [subscription]);

  useEffect(() => {
    if (setupParam === "success") {
      trackTrialPaymentMethodAdded(subscription?.payment_method_status);
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams, setupParam, subscription?.payment_method_status]);

  useEffect(() => {
    if (!isPostCheckout || isActiveSubscription) {
      setActivationTimedOut(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPolling(false);
      setActivationTimedOut(true);
    }, ACTIVATION_PENDING_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isActiveSubscription, isPostCheckout]);

  useEffect(() => {
    if (billingState === "already_subscribed" || isActiveSubscription) {
      clearSelectedPlanKey();
    }
  }, [billingState, isActiveSubscription]);

  const showProcessingBanner = isPostCheckout && !isActiveSubscription && !activationTimedOut;

  const billingActionError = useMemo(() => {
    if (!customerPortalMutation.isError) {
      return null;
    }
    return getBillingActionErrorMessage(customerPortalMutation.error);
  }, [customerPortalMutation.error, customerPortalMutation.isError]);

  const handleManageBilling = () => {
    customerPortalMutation.mutate();
  };

  const handleSetupPaymentMethod = () => {
    trialPaymentMethodMutation.mutate();
  };

  const handleRetryTrial = () => {
    trialRetryMutation.mutate();
  };

  const handleResumeIncompleteCheckout = () => {
    if (subscription?.plan_key) {
      navigate(`/billing/checkout/confirm?plan=${encodeURIComponent(subscription.plan_key)}`);
      return;
    }
    navigate("/billing/checkout/confirm");
  };

  const handleChooseDifferentPlan = () => {
    clearSelectedPlanKey();
    navigate("/billing");
  };

  const handleChoosePlan = (plan: PublicBillingPlan) => {
    if ((isTrialSubscription || isPausedTrial) && plan.plan_id) {
      trialPlanMutation.mutate(
        { plan_id: plan.plan_id },
        {
          onSuccess: (data) => {
            trackTrialPlanSelected({
              trial_plan_id: subscription?.trial_plan_id,
              trial_plan_key: subscription?.trial_plan_key,
              selected_plan_id: data.subscription.selected_post_trial_plan.plan_id,
              selected_plan_key: data.subscription.selected_post_trial_plan.plan_key,
              payment_method_status: data.subscription.payment_method_status,
            });
          },
        }
      );
      return;
    }
    trackPlanSelected({
      plan_id: plan.plan_id,
      plan_key: plan.plan_key,
      plan_name: plan.display_name,
      billing_period: "monthly",
      price: plan.price_monthly,
      currency: plan.currency,
    });
    navigate(getCheckoutConfirmationPath(plan.plan_key));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (subscriptionError) {
    return (
      <Box sx={{ maxWidth: 760, mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h2" gutterBottom>
          Billing
        </Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetchSubscription()}>
              Retry
            </Button>
          }
        >
          We could not load your subscription status. Retry before managing billing or starting checkout.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", px: 3, py: 4 }}>
      <Typography variant="h2" gutterBottom>
        Billing
      </Typography>

      {billingState === "already_subscribed" && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>You already have an active subscription.</strong> Your current plan is{" "}
          {subscription?.plan_name ?? "your current plan"}. Plan changes are not available in this version yet.
          <br />
          You can update your payment method, view invoices, or cancel from Manage billing.
        </Alert>
      )}

      {showProcessingBanner && (
        <Alert icon={<HourglassTopRoundedIcon />} severity="info" sx={{ mb: 3 }}>
          <strong>Confirming your subscription</strong> Your payment was successful. We’re confirming your
          subscription and activating your account.
        </Alert>
      )}

      {activationTimedOut && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Activation pending</strong> This usually completes shortly. Refresh this page if your plan
          does not update automatically. If this continues, contact support.
        </Alert>
      )}

      {checkoutCanceled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Checkout wasn’t completed. You can continue to secure checkout again when you’re ready.
        </Alert>
      )}

      {billingActionError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {billingActionError}
        </Alert>
      )}

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          p: 3,
          backgroundColor: "background.paper",
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Current plan
            </Typography>
            <Typography variant="h3">
              {subscription?.plan_name ?? "No active subscription"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1">{statusLabel}</Typography>
          </Box>

          {timingLabel && formattedTimingDate && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                {timingLabel}
              </Typography>
              <Typography variant="body1">{formattedTimingDate}</Typography>
            </Box>
          )}

          {subscription?.status === "past_due" && (
            <Alert severity="warning">
              Your subscription needs billing attention. AI services are paused until billing is restored.
            </Alert>
          )}

          {subscription?.status === "trialing" && (
            <Alert severity="info">
              <strong>{formatTrialPlanName(subscription)} trial active</strong>
              {subscription.trial_end ? ` Your trial ends on ${formatDate(subscription.trial_end)}.` : ""}
              {typeof subscription.days_remaining === "number"
                ? ` ${subscription.days_remaining} days remaining.`
                : ""}
            </Alert>
          )}

          {subscription?.selected_post_trial_plan && (
            <Alert severity="success">
              <strong>Future plan selected:</strong>{" "}
              {subscription.selected_post_trial_plan.display_name}. Your first billing date is{" "}
              {formatDate(subscription.trial_end ?? subscription.current_period_end)}.
            </Alert>
          )}

          {subscription?.status === "paused" && (
            <Alert severity="warning">
              <strong>Trial ended.</strong> AI services are paused. Choose a plan and add a payment method to reactivate.
            </Alert>
          )}

          {subscription?.trial_provisioning_status === "failed" && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={handleRetryTrial} disabled={trialRetryMutation.isPending}>
                  Retry
                </Button>
              }
            >
              Trial setup did not complete. Retry trial setup before continuing.
            </Alert>
          )}

          {subscription?.status === "incomplete" && (
            <Alert severity="info">
              <strong>Complete your subscription</strong> Your subscription wasn’t completed. Continue to secure
              checkout to activate your selected plan. You haven’t been charged.
            </Alert>
          )}

          {isTrialSubscription || isPausedTrial ? (
            <Stack spacing={2.5}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleSetupPaymentMethod}
                  disabled={trialPaymentMethodMutation.isPending}
                >
                  {trialPaymentMethodMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Manage payment method"
                  )}
                </Button>
              </Stack>

              {trialPlanMutation.isError && (
                <Alert severity="error">{getBillingActionErrorMessage(trialPlanMutation.error)}</Alert>
              )}

              {publicPlansLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading available plans…
                  </Typography>
                </Box>
              )}

              {!publicPlansLoading && !publicPlansError && publicPlans.length > 0 && (
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ alignItems: "stretch" }}>
                  {publicPlans.map((plan) => (
                    <Box key={plan.plan_key} sx={{ flex: 1, minWidth: 0 }}>
                      <PlanChoiceCard
                        plan={plan}
                        onChoose={handleChoosePlan}
                        actionLabel={`Use ${plan.display_name} after trial`}
                        previouslyViewed={plan.plan_key === marketingPlanInterest}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          ) : subscription?.status === "incomplete" ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button variant="contained" onClick={handleResumeIncompleteCheckout}>
                Continue to checkout
              </Button>
              <Button variant="text" onClick={handleChooseDifferentPlan}>
                Choose a different plan
              </Button>
            </Stack>
          ) : !subscription ? (
            <Stack spacing={2.5}>
              <Alert severity="info">
                You do not have an active subscription yet. Choose a plan to continue to checkout.
              </Alert>

              {publicPlansLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading available plans…
                  </Typography>
                </Box>
              )}

              {publicPlansError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={() => refetchPublicPlans()}>
                      Retry
                    </Button>
                  }
                >
                  We could not load subscription plans. Retry when you’re ready.
                </Alert>
              )}

              {!publicPlansLoading && !publicPlansError && publicPlans.length > 0 && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ alignItems: "stretch" }}
                >
                  {publicPlans.map((plan) => (
                    <Box key={plan.plan_key} sx={{ flex: 1, minWidth: 0 }}>
                      <PlanChoiceCard plan={plan} onChoose={handleChoosePlan} />
                    </Box>
                  ))}
                </Stack>
              )}

              {!publicPlansLoading && !publicPlansError && publicPlans.length === 0 && (
                <Alert severity="warning">
                  No subscription plans are available right now. Retry shortly or contact support.
                </Alert>
              )}
            </Stack>
          ) : (
            <Box>
              <Button
                variant="contained"
                startIcon={!customerPortalMutation.isPending ? <ManageAccountsRoundedIcon /> : undefined}
                onClick={handleManageBilling}
                disabled={!subscription || customerPortalMutation.isPending}
              >
                {customerPortalMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Manage billing"
                )}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default BillingPage;
